import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { QrCode, LogIn } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { toast } from "sonner";
import { ShopLanguageSelector } from "../components/ShopLanguageSelector";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import ShopFooter from "../components/ShopFooter";

export default function GalleryLogin() {
  const [, navigate] = useLocation();
  // Manual query param parsing for wouter
  const search = typeof window !== 'undefined' ? window.location.search : '';
  let nextUrl: string | null = null;
  if (search) {
    const params = new URLSearchParams(search);
    nextUrl = params.get('next');
  }
  const { t, language } = useTranslation();
  const [projectNumber, setProjectNumber] = useState("");
  const [code, setCode] = useState("");
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  
  const { data: systemNameData } = trpc.settings.getPublicSystemName.useQuery(undefined, { staleTime: 60_000 });
  const systemName = systemNameData?.systemName || "Okulus";

  const validateMutation = trpc.gallery.validateCode.useMutation({
    onSuccess: (data) => {
      // Store session data in localStorage
      localStorage.setItem("gallery_session", JSON.stringify({
        projectId: data.projectId,
        projectTitle: data.projectTitle,
        cartId: data.cartId,
        token: data.token,
        photographerId: data.photographerId,
      }));
      
      // Set login timestamp for auto-logout
      localStorage.setItem("gallery_login_time", Date.now().toString());
      
      // Navigate to target if provided, else package selection
      if (nextUrl) {
        try {
          const url = new URL(nextUrl, window.location.origin);
          if (url.origin === window.location.origin) {
            navigate(url.pathname + url.search + url.hash);
            return;
          }
        } catch (_) {
          // ignore parse errors and fallback
        }
      }
      navigate("/gallery/packages");
    },
    onError: (error) => {
      if (error.message && error.message.startsWith("Zugangscode bereits")) {
        const match = error.message.match(/bereits (\d+)× verwendet \(Limit (\d+)\)/);
        if (match) {
          const count = match[1];
          const limit = match[2];
          toast.error(`${t.gallery.accessCodeLimitReached} ${count}× (Limit ${limit})`);
          return;
        }
        toast.error(t.gallery.accessCodeLimitReached);
        return;
      }
      if (error.message === 'PROJECT_NOT_ACTIVE') {
        setNotifyOpen(true);
        return;
      }
      toast.error(error.message || t.gallery.invalidCode);
    },
  });

  const subscribeMutation = trpc.gallery.subscribeToActivation.useMutation({
    onSuccess: () => {
      setNotifyOpen(false);
      setNotifyEmail("");
      toast.success(t.gallery.notifySuccess);
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const projectNum = parseInt(projectNumber);
    if (isNaN(projectNum) || !code) {
      toast.error(t.validation.required);
      return;
    }

    validateMutation.mutate({
      projectNumber: projectNum,
      code: code.toUpperCase(),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex flex-col">
      <div className="w-full max-w-md space-y-6 flex-1 flex flex-col justify-center mx-auto">
        <div className="flex justify-end">
          <ShopLanguageSelector />
        </div>
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <BrandLogo size={56} rounded />
            </div>
            <CardTitle className="text-2xl">{systemName}</CardTitle>
            <CardDescription>{t.gallery.title}</CardDescription>
          </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="projectNumber">{t.gallery.projectNumber}</Label>
              <Input
                id="projectNumber"
                type="text"
                inputMode="numeric"
                placeholder="123456"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value)}
                maxLength={6}
                className="text-center text-lg font-mono"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="code">{t.gallery.accessCode}</Label>
              <Input
                id="code"
                type="text"
                placeholder="XXXX-XXXX"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={9}
                className="text-center text-lg font-mono"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={validateMutation.isPending}
            >
              {validateMutation.isPending ? (
                t.common.loading
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  {t.gallery.login}
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-2">
              {t.gallery.scanQRCode}
            </p>
            <div className="flex justify-center">
              <div className="p-4 border-2 border-dashed border-border rounded-lg">
                <QrCode className="h-16 w-16 text-muted-foreground" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {t.gallery.scanInstruction}
            </p>
          </div>
        </CardContent>
        </Card>

        <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t.gallery.projectNotActiveTitle || 'Galerija još nije dostupna'}</DialogTitle>
              <DialogDescription>{t.gallery.projectNotActiveBody || 'Ostavite svoj email i obavijestit ćemo vas čim sadržaj bude objavljen.'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label htmlFor="notifyEmail">{t.gallery.yourEmail || 'Vaš email'}</Label>
              <Input id="notifyEmail" type="email" value={notifyEmail} onChange={(e)=>setNotifyEmail(e.target.value)} placeholder={t.gallery.emailPlaceholder||'you@example.com'} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setNotifyOpen(false)}>{t.common.cancel}</Button>
              <Button onClick={()=>{
                const projectNum = parseInt(projectNumber);
                if (!notifyEmail || isNaN(projectNum)) { toast.error(t.validation?.required || 'Required'); return; }
                subscribeMutation.mutate({ projectNumber: projectNum, email: notifyEmail, language });
              }}>{t.gallery.notifyMe || 'Obavijesti me'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <ShopFooter />
    </div>
  );
}
